import BasePDFDefinitionColumnBuilder, * as BaseColumnBuilder from "../BasePDFDefinitionColumnBuilder"
import BalanceType from "../../enums/BalanceType";
import {LeaveTypeColumnConfig} from "../../reports/leavesReport/LeaveReportConfig";
import {cutLongText} from "../../util/StringUtils";
import {PaperOrientation} from "../constants";
import messageResource from "../../messageResource"
import PDFImageCache from "../PDFImageCache"
import {ReportGroupingLevel} from "../../reports/constants";
import {parameters} from "./PDFDefinitionBuilder"

class PDFDefinitionColumnBuilder extends BasePDFDefinitionColumnBuilder {
    constructor(){
        super()
        this.reportGroupingValues = ["reportFirstGroupingValue", "reportThirdGroupingValue"]
    }

    init(config, rowSpanNumber)
    {
        super.init(config, rowSpanNumber ? rowSpanNumber : 2);
        return this;
    }

    pushWidth(destination, sizes) {}
    makeHeader() {}

    makeRow(lvl, data)
    {
        return this._makeRow(this.reportGroupingValues[lvl], data);
    }

    makeSubHeader() {return ''}
    makeThirdLvlHeader() {return ''}

    makeFirstRow(data) {return this.makeRow(0, data)}
    makeSecondRow(data) {return this.makeRow(1, data)}
}

export class Levels extends BaseColumnBuilder.Levels {
    init(config, leavesNumber, hasLeaves, hasPto, hasSick, store, paperOrientation) {
        super.init(config, 2);

        this.store = store;
        this.groupingLevels = [config.firstGroupingLevel, config.secondGroupingLevel];
        this.reportGroupingStyles = [
            ["reportFirstGroupingRow", "reportFirstGroupingRowMargin"],
            ["reportThirdGroupingRow", "reportSecondGroupingRowMargin"]
        ];

        this.leavesNumber = leavesNumber;
        this.hasLeaves = hasLeaves;
        this.hasPto = hasPto;
        this.hasSick = hasSick;
        this.maxLen = this.getMaxLen(paperOrientation)

        return this;
    }

    getMaxLen(paperOrientation) {
        var columnWidth = paperOrientation === PaperOrientation.LANDSCAPE ? parameters.Landscape.columnWidth
            : parameters.Portrait.columnWidth

        var baseWidth = columnWidth.levels;
        var width = 100 - (this.hasLeaves ? columnWidth["totals"] : 0);
        width -= this.leavesNumber * columnWidth["leave"];
        width -= this.hasPto ? 2 * columnWidth["balance"] : 0;
        width -= this.hasSick ? 2 * columnWidth["balance"] : 0;

        if (width > baseWidth)
            baseWidth = width;

        return baseWidth < 15 ? 10 : baseWidth > 55 ? 32 : (baseWidth - columnWidth.levels) / 35 * 22 + 10;
    }

    _makeRow(style, groupingLevel, identity) {
        var text = this._getTitle(groupingLevel, identity, this.maxLen);
        return this._makeRowDefinition(style, text);
    }

    pushWidth(destination, sizes) {
        var baseWidth = sizes["levels"];
        var width = 100 - (this.hasLeaves ? sizes["totals"] : 0);
        width -= this.leavesNumber * sizes["leave"];
        width -= this.hasPto ? 2 * sizes["balance"] : 0;
        width -= this.hasSick ? 2 * sizes["balance"] : 0;

        if (width > baseWidth)
            baseWidth = width;

        destination.push(baseWidth + '%');
    }

    makeHeader() {
        var firstLevelName = LeavesReportFormatUtils.formatGroupingLevel(this.groupingLevels[0]);
        var secondLevelName = ( this.groupingLevels[1] == ReportGroupingLevel.NONE) ? "" : ' / ' + LeavesReportFormatUtils.formatGroupingLevel(
            this.groupingLevels[1]);
        return this._makeHeaderDefinition(firstLevelName + secondLevelName, [this.tableHeaderStyleNoMargin, "reportLevelsTitle"]);
    }

    makeTotals() {
        return this._makeRowDefinition("reportTotal", messageResource.getMessage("report.total"));
    }

    makeRow(lvl, data) {
        return this._makeRow(this.reportGroupingStyles[lvl], this.groupingLevels[lvl], data.id);
    }
}

export class Totals extends BaseColumnBuilder.Totals {
    constructor() {
        super();
        this.reportGroupingValues = ["reportFirstGroupingValue", "reportThirdGroupingValue"]
    }

    init(config) {
        super.init(config, 2);

        this.onlyLeaves = (config.columnsConfig === LeaveTypeColumnConfig.LEAVE_TIME_ONLY);
        return this;
    }

    makeHeader() {
        var text = messageResource.getMessage("common.leave.reports.pdf.total_leave_time") +
                   "\n(" + LeavesReportFormatUtils.formatTimeFormat(this.timeFormat) + ")";

        return this.onlyLeaves ? this._makeHeaderDefinition(text) : this._makeHeaderDefinition(text, [this.tableHeaderStyleNoMargin, "reportTotalTitle"]);
    }

    _getValue(data) {
        return data.total;
    }
}

export class Balance extends PDFDefinitionColumnBuilder {

    init(config, type, defaultWorkDayDuration) {
        super.init(config);

        this.type = type;
        this.defaultWorkDayDuration = defaultWorkDayDuration;
        var dateRange = config.dateRangeSelection;

        this.templateModel = LeavesReportFormatUtils.makeBalanceTemplateModel(
            this.type, config.firstGroupingLevel, config.secondGroupingLevel, dateRange.fromDate, dateRange.toDate);

        return this;
    }

    pushWidth(destination, sizes) {
        var size = sizes["balance"] + '%';
        destination.push(size);
        destination.push(size);
    }

    makeHeader() {
        return [
            {
                style: this.tableHeaderStyle,
                text: this.templateModel.title,
                colSpan: 2
            }, {}
        ];
    }

    makeSubHeader() {
        var firstColumnText = [
            {text: this.templateModel.startDate},
            {text: this.templateModel.startDateDetails, style: this.smallText},
            {text: " (" + this.templateModel.unit + ")", style: this.smallText}
        ];

        var secondColumnText = [
            {text: this.templateModel.endDate},
            {text: this.templateModel.endDateDetails, style: this.smallText},
            {text: " (" + this.templateModel.unit + ")", style: this.smallText}
        ];

        return [
            {
                style: this.tableHeaderStyle,
                stack: firstColumnText
            },
            {
                style: this.tableHeaderStyle,
                text: secondColumnText
            }
        ];
    }

    getBalance(data) {
        return ( this.type === BalanceType.PTO ) ? data.ptoBalances : data.sickBalances;
    }

    _makeRow(style, data) {
        var balance = this.getBalance(data);
        var startStyle = (balance.start < 0 ) ? [style, this.negativeValue] : style;
        var endStyle = (balance.end < 0 ) ? [style, this.negativeValue] : style;
        return [
            {
                style: startStyle,
                text: LeavesReportFormatUtils.formatBalance(balance.start, this.type, this.defaultWorkDayDuration)
            },
            {
                style: endStyle,
                text: LeavesReportFormatUtils.formatBalance(balance.end, this.type, this.defaultWorkDayDuration)
            }
        ];
    }
}

export class Leave extends PDFDefinitionColumnBuilder {
    init(config, leaveTypeId, leaveTypeIndex) {
        super.init(config);

        this.leaveTypeId = leaveTypeId;
        this.leaveTypeIndex = leaveTypeIndex;
        this.leaveMaxLen = 18;

        this.onlyLeaves = (config.columnsConfig === LeaveTypeColumnConfig.LEAVE_TIME_ONLY);

        return this;
    }

    pushWidth(destination, sizes) {
        destination.push(sizes["leave"] + '%');
    }

    makeHeader() {
        var leaveType = LeaveTypeStore.getLeaveType(this.leaveTypeId);
        var style = this.onlyLeaves ? this.tableHeaderStyle : [this.tableHeaderStyleNoMargin, "reportLeaveTitle"];

        return {
            style: style,
            stack: [
                {width: 9, image: PDFImageCache.getImage("leaveTypeIcon_" + leaveType["iconId"])["image"]},
                {text: "\n" + cutLongText(leaveType.name, this.leaveMaxLen)}
            ],
            rowSpan: 2
        };
    }

    _makeRow(style, data) {
        return this._makeRowDefinition(style, LeavesReportFormatUtils.formatTime(data.leaveTypeTotals[this.leaveTypeIndex], this.timeFormat));
    }
}


// WEBPACK FOOTER //
// ./web/js/pdf/leavesReport/PDFDefinitionColumnBuilder.js